import { defineStore, storeToRefs } from 'pinia';
import { getCurrentScope, onScopeDispose, ref, shallowRef, watch, type Ref } from 'vue';
import { type ProductSearch } from '@/entities/product';
import { useTravelataPinia } from '@/shared/composables';
import type { SerpProduct, SerpResultsDictionaries } from './types';
import { addToSerpResultsDictionaries, createEmptySerpResultsDictionaries } from '../lib/serpResultsDictionaries';
import { toSerpProducts } from '../lib/toSerpProducts';

export const SERP_RESULTS_STORE_ID = 'travelata/serpResults';

export interface SerpResults {
  products: Ref<SerpProduct[]>;
  dictionaries: Ref<SerpResultsDictionaries>;
  isSearching: Ref<boolean>;
  isLoading: Ref<boolean>;
}

const defineSerpResultsStore = defineStore(SERP_RESULTS_STORE_ID, () => {
  const products = shallowRef<SerpProduct[]>([]);
  const dictionaries = shallowRef<SerpResultsDictionaries>(createEmptySerpResultsDictionaries());
  const isSearching = ref(false);
  const isLoading = ref(true);

  return { products, dictionaries, isSearching, isLoading };
});

export function useSerpResults(productSearch: ProductSearch): SerpResults {
  const store = defineSerpResultsStore(useTravelataPinia());
  const { products, dictionaries, isSearching, isLoading } = storeToRefs(store);

  const updateResults = (): void => {
    products.value = toSerpProducts(productSearch.hotels, productSearch.productsByHotel);
    dictionaries.value = addToSerpResultsDictionaries(dictionaries.value, productSearch.products);
  };

  if (productSearch.state !== 'idle') {
    updateResults();
    isSearching.value = productSearch.state === 'loading' || productSearch.state === 'asyncLoading';
  }

  const unsubscribes = [
    productSearch.on('start', () => {
      products.value = [];
      isSearching.value = true;
    }),
    productSearch.on('found', updateResults),
    productSearch.on('syncEnd', updateResults),
    productSearch.on('end', () => {
      updateResults();
      isSearching.value = false;
    }),
  ];

  watch([isSearching, products], () => {
    isLoading.value = isSearching.value && products.value.length === 0;
  });

  if (getCurrentScope()) {
    onScopeDispose(() => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    });
  }

  return { products, dictionaries, isSearching, isLoading };
}
